import { tva } from '@gluestack-ui/utils/nativewind-utils'

import { colors } from '../../tokens'
import type { StatusStripeStatus } from '../status-stripe/StatusStripe'

// Severity tone → status colour for Chip/Severity (Figma node 9321:187313). The
// chip is a neutral `highlight` pill; the tone is carried by the leading dot and,
// when selected, a 2px border (always reserved so selection doesn't shift layout).
export type SeverityTone = 'maintenance' | 'normal' | 'moderate' | 'major' | 'crisis' | 'neutral'

// Shared pill chrome (Figma: highlight bg #2d2d30, hover #37373a, radius 4,
// px-12 py-8, gap-8, h-40). Disabled dims to 40%.
export const chipSeverityContainer = tva({
    base: 'h-10 flex-row items-center gap-2 rounded border-2 bg-surface-highlight px-3 py-2',
    variants: {
        tone: { maintenance: '', normal: '', moderate: '', major: '', crisis: '', neutral: '' },
        isSelected: { true: '', false: 'border-transparent' },
        isDisabled: { true: 'opacity-40', false: '' },
        isInteractive: { true: 'hover:bg-surface-highlight-hover', false: '' },
    },
    compoundVariants: [
        { isSelected: true, tone: 'maintenance', class: 'border-status-neutral' },
        { isSelected: true, tone: 'normal', class: 'border-status-success' },
        { isSelected: true, tone: 'moderate', class: 'border-status-warning' },
        { isSelected: true, tone: 'major', class: 'border-status-caution' },
        { isSelected: true, tone: 'crisis', class: 'border-status-critical' },
        { isSelected: true, tone: 'neutral', class: 'border-status-info' },
    ],
    defaultVariants: { isSelected: false, isDisabled: false, isInteractive: false },
})

export const chipSeverityDot = tva({
    base: 'size-2 rounded-full',
    variants: {
        tone: {
            maintenance: 'bg-status-neutral',
            normal: 'bg-status-success',
            moderate: 'bg-status-warning',
            major: 'bg-status-caution',
            crisis: 'bg-status-critical',
            neutral: 'bg-status-info',
        },
    },
})

// Filled severity ground for badge-style severity marks, paired 1:1 with
// `chipSeverityDot` above. Classes stay literal so Tailwind's scanner sees them.
export const severityToneBackground: Record<SeverityTone, string> = {
    maintenance: 'bg-status-neutral',
    normal: 'bg-status-success',
    moderate: 'bg-status-warning',
    major: 'bg-status-caution',
    crisis: 'bg-status-critical',
    neutral: 'bg-status-info',
}

// Ring counterpart of `severityToneBackground`, for severity marks drawn as an
// outlined circle. Classes stay literal so Tailwind's scanner sees them.
export const severityToneBorder: Record<SeverityTone, string> = {
    maintenance: 'border-status-neutral',
    normal: 'border-status-success',
    moderate: 'border-status-warning',
    major: 'border-status-caution',
    crisis: 'border-status-critical',
    neutral: 'border-status-info',
}

// Status-name counterpart of `severityToneBackground`, for severity marks drawn as
// a leading `StatusStripe` or as a `TimelineBar` fill. `as const` keeps the literal
// union so both components accept it.
export const severityToneStatus = {
    maintenance: 'neutral',
    normal: 'success',
    moderate: 'warning',
    major: 'caution',
    crisis: 'critical',
    neutral: 'info',
} as const satisfies Record<SeverityTone, StatusStripeStatus>

// Raw-value counterpart of `severityToneBackground`, for the surfaces that paint a
// severity through a colour prop instead of a class (React Native icon tiles, SVG).
export const severityToneColor: Record<SeverityTone, string> = {
    maintenance: colors.status.neutral,
    normal: colors.status.success,
    moderate: colors.status.warning,
    major: colors.status.caution,
    crisis: colors.status.critical,
    neutral: colors.status.info,
}

// Figma "Chip/Process" process-selection-grid (AOS-1378): the grid cell is 48px
// tall with a 40px two-line label box (py-4), the single-line pill stays 40px,
// and the vertical cell is 68px = py-8 + 24 icon + gap-8 + 20 label.
export const chipProcessContainer = tva({
    base: 'rounded border-2 bg-surface-highlight px-3',
    variants: {
        layout: {
            'horizontal': 'h-10 flex-row items-center gap-2 py-2',
            'horizontal-2-lines': 'h-12 flex-row items-center gap-2 py-1',
            'vertical': 'flex-col items-center gap-2 py-2',
        },
        isSelected: { true: 'border-action-focus-ring', false: 'border-transparent' },
        isDisabled: { true: 'opacity-40', false: '' },
        isInteractive: { true: 'hover:bg-surface-highlight-hover', false: '' },
    },
    defaultVariants: {
        layout: 'horizontal',
        isSelected: false,
        isDisabled: false,
        isInteractive: false,
    },
})
